import { useState } from "react";
import {
  CommonButton,
  CommonInput,
  CommonSelect,
  CommonDatePicker,
} from "../Input";
import DataTable from "../table/DataTable";
import CreateSingleTransactionModal from "./CreateSingleTransactionModal";
import SendBulkTransactionModal from "./SendBulkTransactionModal";
import { initialData } from "./mockData";
import TransactionInformationModal from "./TransactionInformationModal";
import currencies from "../../../data/currencies";
import { Row, Col, Card } from "react-bootstrap";

const TransactionsList = () => {
  const [showViewModal, setShowViewModal] = useState(false);
  const [showCreateSingleModal, setShowCreateSingleModal] = useState(false);
  const [showCreateBulkModal, setShowCreateBulkModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [filters, setFilters] = useState({
    transactionId: "",
    paymentMethod: null,
    status: null,
    fromDate: "",
    toDate: "",
    sendCurrency: null,
    receiveCurrency: null,
    nameOrPin: "",
  });

  // Data is now imported from mockData.js

  const [displayData, setDisplayData] = useState(initialData);

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setCurrentPage(1);
  };

  const handleSearch = () => {
    let filtered = initialData.filter((item) => {
      // ... existing filter logic
      const matchTransaction =
        !filters.transactionId ||
        item.id.toLowerCase().includes(filters.transactionId.toLowerCase());
      const matchPayment =
        !filters.paymentMethod ||
        item.paymentMethod.toLowerCase() ===
          filters.paymentMethod.label.toLowerCase();
      const matchStatus =
        !filters.status || item.statusValue === filters.status.value;
      const matchNameOrPin =
        !filters.nameOrPin ||
        item.senderName
          .toLowerCase()
          .includes(filters.nameOrPin.toLowerCase()) ||
        item.receiverName
          .toLowerCase()
          .includes(filters.nameOrPin.toLowerCase());

      return matchTransaction && matchPayment && matchStatus && matchNameOrPin;
    });
    setDisplayData(filtered);
    setCurrentPage(1);
  };

  const handleReset = () => {
    setFilters({
      transactionId: "",
      paymentMethod: null,
      status: null,
      fromDate: "",
      toDate: "",
      sendCurrency: null,
      receiveCurrency: null,
      nameOrPin: "",
    });
    setDisplayData(initialData);
    setCurrentPage(1);
  };

  const columns = [
    {
      label: "ID",
      key: "id",
      className: "ps-4",
      cellClassName: "align-middle",
      style: { minWidth: "120px" },
      render: (item) => (
        <span className="fw-bold text-black fs-13">{item.id}</span>
      ),
    },
    {
      label: "Date & Time",
      key: "createdDate",
      className: "ps-4",
      cellClassName: "align-middle",
      style: { minWidth: "120px" },
      render: (item) => (
        <div className="fs-12">
          <div className="fw-medium text-black">{item.displayDate}</div>
          <div className="text-muted">{item.time}</div>
        </div>
      ),
    },
    {
      label: "Sender Name",
      key: "senderName",
      className: "ps-4",
      style: { minWidth: "200px" },
      render: (item) => (
        <div className="fs-12">
          <div className="fw-bold text-black">{item.senderName}</div>
          <div className="text-muted">{item.senderEmail}</div>
        </div>
      ),
    },
    {
      label: "Receiver Name",
      key: "receiverName",
      className: "ps-4",
      cellClassName: "align-middle",
      style: { minWidth: "200px" },
      render: (item) => (
        <span className="text-black fs-13">{item.receiverName}</span>
      ),
    },
    {
      label: "Payment Method",
      key: "paymentMethod",
      className: "ps-4",
      cellClassName: "align-middle",
      style: { minWidth: "140px" },
      render: (item) => (
        <span className="fw-medium text-black fs-13">{item.paymentMethod}</span>
      ),
    },
    {
      label: "Send Amount",
      key: "sendAmount",
      className: "ps-4",
      cellClassName: "align-middle",
      style: { minWidth: "120px" },
      render: (item) => (
        <div className="fs-12">
          <div className="fw-bold text-black fs-13">
            {item.sendAmount} {item.currency}
          </div>
          {item.itemCount && <div className="text-muted">{item.itemCount}</div>}
        </div>
      ),
    },
    {
      label: "Status",
      key: "status",
      className: "ps-4",
      cellClassName: "align-middle",
      style: { minWidth: "200px" },
      render: (item) => {
        let color = "#fff";
        let bgColor = "#4D8DFB";
        let borderColor = "#4D8DFB";

        if (item.statusValue === "waiting_admin") {
          bgColor = "#F59D56";
          borderColor = "#F59D56";
        } else if (item.statusValue === "waiting_aml") {
          color = "#6B72F7";
          bgColor = "#fff";
          borderColor = "#6B72F7";
        }

        return (
          <span
            className="px-2 py-1 border rounded d-inline-block"
            style={{
              color: color,
              backgroundColor: bgColor,
              borderColor: borderColor,
            }}
          >
            {item.status}
          </span>
        );
      },
    },
    {
      label: "Actions",
      key: "actions",
      className: "ps-4 ",
      render: (item) => (
        <div
          className="cursor-pointer text-muted hover-primary"
          onClick={() => {
            setSelectedTransaction(item);
            setShowViewModal(true);
          }}
        >
          <i className="far fa-eye text-secondary fs-16"></i>
        </div>
      ),
    },
  ];

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = displayData.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const paymentMethodOptions = [
    { label: "Cash", value: "cash" },
    { label: "Bank Transfer", value: "bank_transfer" },
  ];

  const currencyOptions = currencies.map((c) => ({
    label: `${c.code} - ${c.name}`,
    value: c.code,
  }));

  const statusOptions = [
    { label: "Waiting admin approval", value: "waiting_admin" },
    { label: "Waiting for AML transaction approval", value: "waiting_aml" },
    { label: "Processing", value: "processing" },
    { label: "Done", value: "done" },
    { label: "Rejected", value: "rejected" },
  ];

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-4 gap-2">
        <h2 className="fw-bold text-black mb-0">Transactions Activity</h2>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <CommonButton
            variant="primary"
            onClick={() => setShowCreateSingleModal(true)}
          >
            Create Single Transaction
          </CommonButton>
          <CommonButton
            variant="outline"
            onClick={() => setShowCreateBulkModal(true)}
          >
            Create Multiple Transaction
          </CommonButton>
        </div>
      </div>

      <div className=" card border-0 shadow-sm rounded-4 mb-4">
        <div className="p-4">
          <Row className="g-3 mb-3">
            <Col md={3}>
              <CommonDatePicker
                label="From date"
                placeholder="Select from date"
                value={filters.fromDate}
                onChange={(date) => handleFilterChange("fromDate", date)}
              />
            </Col>
            <Col md={3}>
              <CommonDatePicker
                label="To date"
                placeholder="Select to date"
                value={filters.toDate}
                onChange={(date) => handleFilterChange("toDate", date)}
              />
            </Col>
            <Col md={3}>
              <CommonSelect
                label="Status"
                placeholder="Select status"
                options={statusOptions}
                value={filters.status}
                onChange={(val) => handleFilterChange("status", val)}
              />
            </Col>
            <Col md={3}>
              <CommonSelect
                label="Send currency"
                placeholder="Select send currency"
                options={currencyOptions}
                value={filters.sendCurrency}
                onChange={(val) => handleFilterChange("sendCurrency", val)}
              />
            </Col>
          </Row>
          <Row className="g-3 align-items-end">
            <Col md={3}>
              <CommonSelect
                label="Receive currency"
                placeholder="Select receive currency"
                options={currencyOptions}
                value={filters.receiveCurrency}
                onChange={(val) => handleFilterChange("receiveCurrency", val)}
              />
            </Col>
            <Col md={3}>
              <CommonInput
                label="Name or pin no."
                placeholder="Enter name or pin no."
                value={filters.nameOrPin}
                onChange={(e) =>
                  handleFilterChange("nameOrPin", e.target.value)
                }
              />
            </Col>
            <Col md={6} className="d-flex gap-2">
              <CommonButton
                onClick={handleReset}
                variant="outline"
                style={{ width: "50px", height: "50px", minWidth: "50px" }}
                className="p-0 d-flex align-items-center justify-content-center"
              >
                <i className="flaticon-381-repeat-1 fs-20"></i>
              </CommonButton>
              <CommonButton
                onClick={handleSearch}
                className="p-0 d-flex align-items-center justify-content-center"
                style={{ width: "50px", height: "50px", minWidth: "50px" }}
              >
                <i className="flaticon-381-search-1 fs-20 text-white"></i>
              </CommonButton>
            </Col>
          </Row>
        </div>
      </div>

      <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
        <div className="card-body">
          <DataTable
            columns={columns}
            data={currentItems}
            totalItems={displayData.length}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            paginate={paginate}
            showPagination={true}
            minWidth="100%"
          />
        </div>
      </div>

      <TransactionInformationModal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        transaction={selectedTransaction}
      />

      <CreateSingleTransactionModal
        show={showCreateSingleModal}
        onHide={() => setShowCreateSingleModal(false)}
      />

      <SendBulkTransactionModal
        show={showCreateBulkModal}
        onHide={() => setShowCreateBulkModal(false)}
      />
    </div>
  );
};

export default TransactionsList;
